import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { AccountTable } from 'src/components/payment/account/AccountTable';
import { AddScaModal } from 'src/components/payment/account/AddScaModal';
import { ScaReadinessCard } from 'src/components/payment/account/ScaReadinessCard';
import { WithdrawalTanModal } from 'src/components/payment/account/WithdrawalTanModal';
import { SiteHeader } from 'src/components/ui/SiteHeader';
import { useMangoAccountInfo } from 'src/hooks/useMangoAccountInfo';
import { useMangoTransactions } from 'src/hooks/useMangoTransactions';
import { usePayoutBySpv } from 'src/hooks/usePayoutBySpv';
import { useSPV } from 'src/hooks/useSPV';
import { useSpvSca } from 'src/hooks/useSpvSca';
import { MangoPayAccount, SPV } from 'src/models';
import { PayoutStatus } from 'src/models/enum';
import { Routes } from 'src/router/Routes';
import { availableAccountBalance } from 'src/util/utils';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { Button, Card, Skeleton, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { TransactionsCSVModal } from '../../components/payment/account/TransactionsCSVModal';

const transactionsStartDate = new Date('2018-10-05T09:43:53.058Z');
const transactionsEndDate = new Date();

export const AccountOverview = () => {
  const { t } = useTranslation();
  const [showTanModal, setShowTanModal] = useState<boolean>(false);
  const [showCSVModal, setShowCSVModal] = useState<boolean>(false);
  const [showScaModal, setShowScaModal] = useState<boolean>(false);
  const [tablePage, setTablePage] = useState(1);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(10);
  const [refresh, setRefresh] = useState<boolean>(false); //
  const { spvId } = useParams();
  const spv = useSPV(spvId, refresh);
  const { refresh: refreshSca } = useSpvSca();
  const mangoId = spv?.paymentProvider.mangopayId ?? null;
  const mangoWalletId = spv?.paymentProvider.mangopayWalletId ?? null;
  const mangoAccount = useMangoAccountInfo(spv?.paymentProvider.mangopayId ?? null, spv?.paymentProvider.mangopayWalletId ?? null, refresh);
  const transactions = useMangoTransactions(
    mangoId,
    mangoWalletId,
    {
      from: transactionsStartDate,
      to: transactionsEndDate,
      page: tablePage.toString(),
      pageSize: tableRowsPerPage.toString(),
    },
    refresh,
  );
  const payoutByAllSpvs = spvId && usePayoutBySpv([spvId], [PayoutStatus.init, PayoutStatus.pending]);
  const payoutBySelectedSpv = spvId && payoutByAllSpvs ? Object.values(payoutByAllSpvs[spvId]) : null;
  const availableBalance = availableAccountBalance(payoutBySelectedSpv, mangoAccount?.balance ?? 0, true);
  const allowCSVDownload = mangoId && mangoWalletId;
  const hasMangopayAccount = Boolean(mangoId);

  const handlePageChange = (page: number) => {
    setTablePage(page);
  };

  const handleRowChange = (rows: number) => {
    setTableRowsPerPage(rows);
  };

  const handleCloseTanModal = () => {
    setShowTanModal(false);
  };

  const handleCloseAfterSuccess = () => {
    setShowTanModal(false);
    setRefresh(!refresh);
  };

  const handleCloseScaModalAfterSuccess = () => {
    setShowScaModal(false);
    setRefresh(!refresh);
  };

  const handleRefreshSca = async () => {
    if (!spvId) return;
    await refreshSca(spvId);
    setRefresh(!refresh);
  };

  const cardStyle = {
    height: '300px',
  };

  const returnAccountInfo = (spv: SPV | null, mangoAccount: MangoPayAccount | null, referenceAccount: boolean) => {
    return (
      <>
        <Typography variant="body1" sx={{ fontWeight: 500, mb: 0 }}>
          {spv?.companyName ?? <Skeleton variant="text" width={100} />}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }} color="GrayText">
          {t('payment.account.accountOwner')}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500, mb: 0 }}>
          {!referenceAccount && (mangoAccount?.IBAN ?? <Skeleton variant="text" width={100} />)}
          {referenceAccount && (mangoAccount?.ReferenceAccountIBAN ?? <Skeleton variant="text" width={100} />)}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }} color="GrayText">
          {t('payment.account.iban')}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500, mb: 0 }}>
          {!referenceAccount && (mangoAccount?.BIC ?? <Skeleton variant="text" width={100} />)}
          {referenceAccount && (mangoAccount?.ReferenceAccountBIC ?? <Skeleton variant="text" width={100} />)}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }} color="GrayText">
          {t('payment.account.bic')}
        </Typography>
      </>
    );
  };
  return (
    <>
      <SiteHeader
        breadcrumbs={[
          {
            link: Routes.PAYMENT.ROOT,
            label: t('payment.breadcrumbs.payment'),
            icon: <AccountBalanceIcon sx={{ mr: 0.5 }} fontSize="inherit" />,
          },
          {
            link: spvId ? Routes.PAYMENT.ACCOUNT.replace(':spvId', spvId) : '#',
            label: spv?.companyName ?? ' ',
          },
        ]}
        headline={spv?.companyName ?? ' '}
      />
      <Grid container spacing={2}>
        <Grid xs={4}>
          <Card sx={{ p: 2 }} style={cardStyle}>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 3 }}>
              {t('payment.account.paymentAccount')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 0 }}>
              {mangoAccount ? '€ ' + mangoAccount.balance : <Skeleton variant="text" width={100} />}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }} color="GrayText">
              {t('payment.account.availableFunds')}
            </Typography>
            {returnAccountInfo(spv, mangoAccount, false)}
          </Card>
        </Grid>
        <Grid xs={4}>
          <Card sx={{ p: 2 }} style={cardStyle}>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 3 }}>
              {t('payment.account.referenceaccount')}
            </Typography>
            {returnAccountInfo(spv, mangoAccount, true)}
          </Card>
        </Grid>
        <Grid>
          <Grid container spacing={2}>
            <Grid>
              <Button variant="contained" onClick={() => setShowTanModal(true)}>
                {t('payment.account.withdrawal')}
              </Button>
            </Grid>
            <Grid>
              <Button variant="contained" to={Routes.PAYMENT.RENT.replace(':spvId', spvId ?? '')} component={Link}>
                {t('payment.account.investorPayout')}
              </Button>
            </Grid>
            {allowCSVDownload && (
              <Grid>
                <Button variant="contained" onClick={() => setShowCSVModal(true)}>
                  {t('payment.account.csv.download')}
                </Button>
              </Grid>
            )}
            <Grid>
              {/* Without a Mangopay account there is nothing to enrol or consent to — both calls
                  would fail server-side. Keep the button visible but inert, and say why. */}
              <Tooltip title={hasMangopayAccount ? '' : t('payment.account.sca.notSetUp.description')}>
                <span>
                  <Button variant="contained" disabled={!hasMangopayAccount} onClick={() => setShowScaModal(true)}>
                    {t('payment.account.sca.addSca')}
                  </Button>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <div style={{ marginTop: '20px' }}></div>
      <Grid container spacing={2}>
        <Grid xs={8}>
          <ScaReadinessCard readiness={spv?.mangopayReadiness} onRefresh={handleRefreshSca} />
        </Grid>
      </Grid>
      <div style={{ marginTop: '20px' }}></div>
      {transactions && (
        <AccountTable
          transactions={transactions}
          page={tablePage}
          onPageChange={handlePageChange}
          rowsPerPage={tableRowsPerPage}
          onRowChange={handleRowChange}
        />
      )}
      {spvId && (
        <WithdrawalTanModal
          open={showTanModal}
          handleClose={handleCloseTanModal}
          availableFunds={availableBalance as string}
          spvId={spvId}
          handleCloseAfterSuccess={handleCloseAfterSuccess}
        />
      )}
      {allowCSVDownload && showCSVModal && (
        <TransactionsCSVModal handleClose={() => setShowCSVModal(false)} mangoId={mangoId} mangoWalletId={mangoWalletId} />
      )}
      {spvId && (
        <AddScaModal
          open={showScaModal}
          handleClose={() => setShowScaModal(false)}
          spvId={spvId}
          handleCloseAfterSuccess={handleCloseScaModalAfterSuccess}
        />
      )}
    </>
  );
};
